import { useState } from "react";
import { Link } from "react-router-dom";
import { Check, RotateCw, ArrowRight } from "lucide-react";
import AbstractShape from "@/components/AbstractShape";
import MelbiVial from "@/components/MelbiVial";
import PriceLine from "@/components/PriceLine";
import useSEO from "@/lib/useSEO";
import { organizationSchema, websiteSchema, faqPageSchema } from "@/lib/schema";
import { TREATMENTS } from "@/lib/treatments";
import { goToIntake } from "@/lib/intake";
import { useHorizontalScroll, ScrollArrows } from "@/components/HorizontalScroller";
import HomeFAQ, { HOME_FAQS } from "@/components/HomeFAQ";

// Transparent space below each vial photo, as a fraction of image height.
// Tirzepatide card is the alignment reference; other vials shift up to share its baseline.
const VIAL_REF_PAD = 0.152;
const VIAL_BOTTOM_PAD = {
  "weight-loss": 0.152, semaglutide: 0.152, tirzepatide: 0.152, "skin-care": 0.149, vitality: 0.104,
  recovery: 0.104, "metabolic-support": 0.1, antioxidant: 0.107, energy: 0.104,
};

const homeTreatments = TREATMENTS.filter((t) => !t.hiddenFromHomeGrid)
  .sort((a, b) => (b.slug === "weight-loss") - (a.slug === "weight-loss"))
  .flatMap((t) => {
    const base = {
      ...t,
      cardKey: t.slug,
      href: `/treatments/${t.slug}`,
      cardEyebrow: t.cardEyebrow || t.treatment.replace(/\s*Injection$/, ""),
    };
    return t.homeCards ? t.homeCards.map((c) => ({ ...base, ...c })) : [base];
  });

const TRUSTPILOT_URL = "https://www.trustpilot.com/review/melbiwellness.com";
const TRUSTPILOT_LINK_CLASS =
  "inline-flex items-center gap-1.5 text-sm font-semibold text-[#2C2B29] hover:text-[#C96A4E] underline underline-offset-4 decoration-[#E8E5DF] hover:decoration-[#C96A4E] transition-colors";

const steps = [
  {
    n: "01",
    t: "Choose your wellness goal",
    d: "Explore focused wellness options built around how you want to look, feel, and perform.",
    img: "/lifestyle/step1.png",
  },
  {
    n: "02",
    t: "Complete a secure intake",
    d: "Share your health history, medications, allergies, and safety information through a secure online form.",
    img: "/lifestyle/our-story.png",
  },
  {
    n: "03",
    t: "Get clinician review",
    d: "A licensed clinician reviews your intake and may approve, deny, request more information, or recommend a brief live visit.",
    img: "/lifestyle/step3.png",
  },
  {
    n: "04",
    t: "Pharmacy fulfillment",
    d: "If clinically appropriate, your prescription is sent to a licensed pharmacy for fulfillment and direct shipping.",
    img: "/lifestyle/step4.png",
  },
];

const pricingTiers = [
  {
    name: "Monthly plan",
    tagline: "Billed monthly.",
    // Light terracotta tint — entry tier
    tone: {
      bg: "#FBEEE5",
      border: "#EFD3C0",
      title: "#2C2B29",
      body: "#5E5C58",
    },
  },
  {
    name: "3-month plan",
    tagline: "Pay every three months at a lower monthly equivalent.",
    bestValue: true,
    // Medium terracotta — middle tier
    tone: {
      bg: "#F2D0BD",
      border: "#D89B85",
      title: "#2C2B29",
      body: "#5E5C58",
    },
  },
];

const everyProgramIncludes = [
  "Licensed clinician review",
  "Pharmacy fulfillment, if prescribed",
  "Cancel anytime",
];

function FlipStepCard({ step }) {
  const [flipped, setFlipped] = useState(false);
  const toggle = (e) => {
    if (e) e.stopPropagation();
    setFlipped((v) => !v);
  };
  return (
    <div
      className={`flip-card h-48 md:h-56 ${flipped ? "is-flipped" : ""}`}
      data-testid={`how-it-works-card-${step.n}`}
      onClick={toggle}
      role="button"
      aria-pressed={flipped}
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          toggle();
        }
      }}
    >
      <div className="flip-card-inner">
        {/* Front */}
        <div className="flip-card-face overflow-hidden bg-white border border-[#E8E5DF]">
          {step.img && (
            <img
              src={step.img}
              alt=""
              aria-hidden="true"
              className="absolute inset-0 w-full h-full object-cover"
              loading="lazy"
              draggable={false}
            />
          )}
          {/* Soft gradient overlay for legible text */}
          <div
            className="absolute inset-0"
            style={{
              background:
                "linear-gradient(180deg, rgba(44,43,41,0.05) 0%, rgba(44,43,41,0.55) 100%)",
            }}
            aria-hidden="true"
          />
          {/* Step number badge */}
          <span className="absolute top-2 left-2 md:top-3 md:left-3 inline-flex items-center justify-center w-7 h-7 md:w-8 md:h-8 rounded-full bg-white/95 font-display text-xs md:text-sm font-bold text-[#C96A4E] shadow-sm">
            {step.n}
          </span>
          {/* Tap-to-flip hint */}
          <span
            className="flip-card-flip-hint absolute top-2 right-2 md:top-3 md:right-3 inline-flex items-center gap-1.5 bg-white/85 backdrop-blur-sm text-[#2C2B29] rounded-full pl-2 pr-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.14em] shadow-sm transition-colors"
            aria-hidden="true"
          >
            <RotateCw size={11} strokeWidth={2.5} />
            Tap
          </span>
          {/* Title bottom-left */}
          <h3 className="absolute left-3 right-3 bottom-3 md:left-4 md:right-4 md:bottom-4 font-display text-base md:text-2xl font-semibold text-white leading-tight drop-shadow-[0_1px_2px_rgba(0,0,0,0.35)]">
            {step.t}
          </h3>
        </div>
        {/* Back */}
        <div className="flip-card-face flip-card-back bg-[#C96A4E] border border-[#B35A40] p-3 md:p-7 justify-start text-left">
          <span className="font-display text-sm md:text-lg font-bold text-white/70">{step.n}</span>
          <p className="mt-1 md:mt-3 text-sm md:text-lg text-white leading-snug md:leading-relaxed">{step.d}</p>
        </div>
      </div>
    </div>
  );
}

export default function Home() {
  const carousel = useHorizontalScroll();
  useSEO({
    title: "Melbi Wellness | The Best Version of You Starts Here",
    description:
      "Optimize your wellness with clinician-guided care. 100% online, no membership required, licensed U.S. pharmacy fulfillment. Available in all 50 states.",
    url: "https://melbiwellness.com/",
    image: "https://melbiwellness.com/og/default.png?v=11",
    jsonLd: [
      organizationSchema(),
      websiteSchema(),
      faqPageSchema(HOME_FAQS),
    ],
  });

  const trackEvent = (name) => {
    if (typeof window !== "undefined" && typeof window.posthog?.capture === "function") {
      try {
        window.posthog.capture(name);
      } catch (err) {
        if (process.env.NODE_ENV !== "production") {
          console.debug("[home] posthog capture failed:", err);
        }
      }
    }
  };

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative overflow-hidden">
        <AbstractShape
          variant="gradient-warm"
          colors={["#F5E6D3", "#E8D5C4", "#D4DCD7"]}
          className="absolute inset-0"
          testId="hero-bg"
        />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-3 pb-1 md:pt-16 md:pb-9">
          <div className="max-w-3xl md:mx-auto md:text-center animate-fade-up">
            <h1
              data-testid="hero-headline"
              className="font-display text-[clamp(2.25rem,10.5vw,2.625rem)] sm:text-[3.5rem] lg:text-[4.5rem] font-bold leading-[1.05] tracking-tight text-[#2C2B29]"
            >
              The best version of you{" "}
              <span className="text-[#C96A4E]">starts here.</span>
            </h1>
            <p
              data-testid="hero-subhead"
              className="mt-4 md:mt-8 font-display text-[1.3125rem] sm:text-2xl md:text-[1.625rem] text-[#2C2B29] leading-[1.25] md:leading-[1.2] tracking-tight max-w-2xl md:max-w-none md:whitespace-nowrap md:mx-auto"
            >
              Optimize your wellness
              <br className="sm:hidden" />{" "}
              <em className="italic text-[#C96A4E] font-normal">with clinician-guided care.</em>
            </p>
            <ul className="mt-4 md:mt-8 grid grid-cols-2 gap-x-3 sm:gap-x-6 gap-y-2 text-base md:text-[17px] text-[#2C2B29] max-w-2xl md:mx-auto md:w-fit md:text-left">
              {[
                "No membership fees",
                "Licensed clinician care",
                "Free expedited shipping",
                "Licensed U.S. pharmacy fulfillment",
              ].map((p) => (
                <li key={p} className="flex items-start gap-1.5 sm:gap-2">
                  <Check size={18} className="mt-0.5 text-[#C96A4E] shrink-0" />
                  <span className="leading-snug">{p}</span>
                </li>
              ))}
            </ul>
            <div className="mt-5 md:mt-7 flex flex-col gap-3 sm:items-start md:items-center">
              <button
                type="button"
                onClick={goToIntake}
                data-testid="hero-primary-cta"
                className="inline-flex items-center justify-center bg-[#C96A4E] text-white rounded-full px-8 py-2.5 sm:py-4 text-base font-semibold hover:bg-[#B35A40] transition-colors shadow-sm"
              >
                Start now
                <ArrowRight size={18} className="ml-2" />
              </button>
              <Link
                to="/#how-it-works"
                onClick={() => trackEvent("cta_home_secondary_how_it_works")}
                data-testid="hero-secondary-cta"
                className="self-center sm:self-start md:self-center inline-flex items-center gap-1.5 text-sm font-semibold text-[#2C2B29] hover:text-[#C96A4E] underline underline-offset-4 decoration-[#E8E5DF] hover:decoration-[#C96A4E] transition-colors"
              >
                See How It Works
              </Link>
              <a
                href={TRUSTPILOT_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="hero-trustpilot-link"
                className={`self-center sm:self-start md:self-center ${TRUSTPILOT_LINK_CLASS}`}
              >
                Read our reviews on Trustpilot
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* WELLNESS CATEGORIES */}
      <section
        id="wellness-categories"
        className="bg-[#F3F1EC]/50 border-y border-[#E8E5DF] scroll-mt-24"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 md:py-14">
        <div className="flex items-end justify-between gap-6">
          <div className="max-w-2xl">
            <h2 className="font-display text-2xl md:text-4xl font-semibold tracking-tight text-[#2C2B29]">
              The Wellness Edit
            </h2>
            <p className="mt-3 text-[#5E5C58] text-base md:text-xl leading-relaxed">
              Helping you look, feel, and perform at your best.
            </p>
          </div>
          <ScrollArrows edges={carousel.edges} scrollBy={carousel.scrollBy} testIdPrefix="wellness-carousel" />
        </div>
        <div
          ref={carousel.ref}
          onScroll={carousel.update}
          data-testid="wellness-carousel"
          className="no-scrollbar mt-6 md:mt-8 -mx-4 px-4 sm:-mx-6 sm:px-6 lg:-mx-2 lg:px-2 py-2 flex gap-3 md:gap-5 overflow-x-auto snap-x snap-mandatory scroll-px-4 sm:scroll-px-6 lg:scroll-px-2 overscroll-x-contain"
        >
          {homeTreatments.map((t) => (
            <Link
              key={t.cardKey}
              to={t.href}
              data-testid={`treatment-card-${t.cardKey}`}
              onClick={() => {
                if (typeof window !== "undefined" && typeof window.posthog?.capture === "function") {
                  try {
                    window.posthog.capture(`cta_category_card_${t.cardKey.replace(/-/g, "_")}`);
                  } catch (err) {
                    if (process.env.NODE_ENV !== "production") {
                      console.debug("[home] posthog capture failed:", err);
                    }
                  }
                }
              }}
              style={{ "--vial-pad": VIAL_BOTTOM_PAD[t.primaryVialSlug || t.slug] }}
              className="[--imgh:147px] sm:[--imgh:184px] md:[--imgh:221px] snap-start shrink-0 basis-[42%] md:basis-[calc((100%-2.5rem)/3)] lg:basis-[calc((100%-3.75rem)/4)] group relative bg-white rounded-2xl md:rounded-3xl overflow-hidden border border-[#E8E5DF] hover:border-[#C96A4E]/40 hover:-translate-y-1 transition-all duration-300 shadow-[0_4px_24px_rgba(0,0,0,0.04)] hover:shadow-[0_12px_40px_rgba(201,106,78,0.12)] flex flex-col"
            >
              <div className="relative h-[var(--imgh)] overflow-hidden bg-white shrink-0">
                <div
                  style={{ transform: `translateY(calc((var(--vial-pad) - ${VIAL_REF_PAD}) * var(--imgh)))` }}
                  className="relative z-10 flex items-center justify-center h-full"
                >
                  <MelbiVial
                    slug={t.primaryVialSlug || t.slug}
                    alt={`Melbi ${t.vialLabel}`}
                    testId={`vial-${t.cardKey}`}
                    className="h-full w-auto group-hover:scale-105 transition-transform duration-500 vial-shadow object-contain"
                  />
                  {t.dualVialOnCard && t.secondaryVialSlug && (
                    <MelbiVial
                      slug={t.secondaryVialSlug}
                      alt={`Melbi ${t.secondaryVialSlug} vial`}
                      testId={`vial-secondary-${t.slug}`}
                      className="h-full w-auto -ml-16 sm:-ml-20 md:-ml-24 group-hover:scale-105 transition-transform duration-500 vial-shadow object-contain"
                    />
                  )}
                </div>
              </div>
              <div
                style={{ marginTop: `calc(18px - ${VIAL_REF_PAD} * var(--imgh))` }}
                className="relative z-10 px-3 pb-3 md:px-4 md:pb-4 flex flex-col flex-1"
              >
                <p className="min-h-[2.5em] lg:min-h-0 text-[10px] md:text-xs font-bold uppercase tracking-[0.12em] md:tracking-[0.18em] text-[#8A9A92] leading-tight">
                  {t.cardEyebrow || t.treatment}
                </p>
                <h3 className="mt-1 font-display text-base md:text-xl font-semibold tracking-tight text-[#2C2B29] leading-tight break-words">
                  {t.cardTitle || t.name}
                </h3>
                {t.showOptionsOnCard && (t.cardOptions || t.pricing?.options) ? (
                  <>
                    <ul
                      data-testid={`home-card-options-${t.slug}`}
                      className="mt-1.5 space-y-1"
                    >
                      {(t.cardOptions || t.pricing.options).map((o) => (
                        <li
                          key={o.name}
                          className="text-[11px] md:text-sm text-[#2C2B29] leading-snug"
                        >
                          <span className="font-medium">{o.name}:</span>{" "}
                          <span className="font-semibold text-[#C96A4E]">${o.price}</span>
                          <span className="font-semibold">/month</span>
                          {o.suffix && (
                            <span className="block text-[#8A9A92] font-normal text-[10px] md:text-[11px]">
                              {o.suffix}
                            </span>
                          )}
                        </li>
                      ))}
                    </ul>
                    {t.cardOptionsSharedSuffix && (
                      <p
                        data-testid={`home-card-shared-suffix-${t.slug}`}
                        className="mt-0.5 text-[10px] md:text-[11px] text-[#8A9A92] leading-snug"
                      >
                        {t.cardOptionsSharedSuffix}
                      </p>
                    )}
                  </>
                ) : (
                  <PriceLine
                    treatment={t.cardPricing ? { ...t, pricing: t.cardPricing } : t}
                    size="sm"
                    showFinePrint={false}
                    showNote={false}
                    className="mt-1.5"
                    testId={`home-card-price-${t.cardKey}`}
                  />
                )}
                {t.cardSecondaryNote && (
                  <p
                    data-testid={`home-card-secondary-note-${t.slug}`}
                    className="mt-2 text-[11px] md:text-xs text-[#5E5C58] italic leading-snug"
                  >
                    {t.cardSecondaryNote}
                  </p>
                )}
                <span className="mt-auto pt-3 inline-flex items-center text-xs md:text-sm font-semibold text-[#C96A4E] group-hover:gap-2 gap-1 transition-all">
                  View treatment <ArrowRight size={12} className="md:hidden" />
                  <ArrowRight size={14} className="hidden md:inline" />
                </span>
              </div>
            </Link>
          ))}
        </div>

        {/* Product compliance disclaimer */}
        <p
          data-testid="product-compliance-disclaimer"
          className="mt-8 md:mt-10 text-xs md:text-sm text-[#8A9A92] leading-relaxed max-w-3xl"
        >
          Clinical review is required. Prescriptions are issued
          only when medically appropriate. Not all patients qualify. Results vary.
          Compounded medications are not reviewed by the FDA for safety, effectiveness,
          or quality.
        </p>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section
        id="how-it-works"
        className="scroll-mt-24"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 md:py-10">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div className="max-w-2xl">
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#8A9A92]">
                How It Works
              </p>
              <h2 className="mt-2 font-display text-3xl md:text-4xl font-semibold tracking-tight text-[#2C2B29] leading-tight">
                Clinician-guided care.{" "}
                <span className="italic text-[#C96A4E]">Licensed pharmacy fulfillment.</span>
              </h2>
            </div>
            <button
              type="button"
              onClick={goToIntake}
              data-testid="how-it-works-start-now-cta"
              className="shrink-0 inline-flex items-center bg-[#C96A4E] text-white rounded-full px-5 py-2.5 text-sm font-semibold hover:bg-[#B35A40] transition-colors shadow-sm"
            >
              Start now
              <ArrowRight size={16} className="ml-2" />
            </button>
          </div>
          <div className="mt-4 md:mt-5 grid grid-cols-2 lg:grid-cols-4 gap-2 md:gap-4">
            {steps.map((s) => (
              <FlipStepCard key={s.n} step={s} />
            ))}
          </div>
        </div>
      </section>

      {/* PRICING */}
      <section className="bg-[#F3F1EC]/50 border-y border-[#E8E5DF]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 md:py-14">
        <div className="max-w-2xl">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#8A9A92]">
            Pricing
          </p>
          <h2 className="mt-3 font-display text-3xl md:text-4xl font-semibold tracking-tight text-[#2C2B29]">
            Flexible plan options.
          </h2>
          <p className="mt-4 text-[#5E5C58] leading-relaxed">
            Prescription treatments are only available
            if clinically appropriate after clinician review.
          </p>
        </div>

        {/* Every program includes — single shared row removes repetition from cards */}
        <ul
          data-testid="every-program-includes"
          className="mt-8 flex flex-wrap gap-x-8 gap-y-2 text-sm md:text-base text-[#2C2B29] font-medium"
        >
          {everyProgramIncludes.map((p) => (
            <li key={p} className="flex items-start gap-2">
              <Check size={18} className="mt-0.5 text-[#C96A4E] shrink-0" />
              <span>{p}</span>
            </li>
          ))}
        </ul>
        <p
          data-testid="pricing-renewal-notice"
          className="mt-3 max-w-3xl text-[11px] text-[#566B5D] leading-tight"
        >
          All plans renew automatically until canceled.{" "}
          <Link
            to="/legal/refund"
            data-testid="pricing-cancellation-policy-link"
            className="font-semibold underline underline-offset-2 hover:text-[#C96A4E]"
          >
            Cancel anytime
          </Link>
          .
        </p>

        <div className="mt-8 md:mt-10 max-w-3xl mx-auto grid md:grid-cols-2 gap-4 items-stretch">
          {pricingTiers.map((tier) => (
            <button
              key={tier.name}
              type="button"
              onClick={goToIntake}
              data-testid={`pricing-card-${tier.name.toLowerCase().replace(/\s/g, "-")}`}
              className="group relative flex flex-col text-left rounded-2xl p-5 md:p-6 border transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_10px_36px_rgba(0,0,0,0.06)] shadow-[0_4px_24px_rgba(0,0,0,0.03)] cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-[#C96A4E]/40 focus-visible:ring-offset-2"
              style={{ background: tier.tone.bg, borderColor: tier.tone.border }}
            >
              {tier.bestValue && (
                <span
                  data-testid={`pricing-card-best-value-${tier.name.toLowerCase().replace(/\s/g, "-")}`}
                  className="absolute top-4 right-4 inline-flex items-center gap-1 rounded-full bg-[#2C2B29] text-white px-2 py-0.5 text-[9px] font-bold uppercase tracking-[0.16em] shadow-sm"
                >
                  Best value
                </span>
              )}
              <h3
                className="font-display text-xl md:text-2xl font-semibold leading-tight pr-20"
                style={{ color: tier.tone.title }}
              >
                {tier.name}
              </h3>
              <p
                className="mt-2 text-sm leading-snug"
                style={{ color: tier.tone.body }}
              >
                {tier.tagline}
              </p>
              <span
                className="mt-auto pt-4 inline-flex items-center gap-1 text-xs font-semibold uppercase tracking-[0.18em] transition-all group-hover:gap-2"
                style={{ color: tier.tone.title }}
              >
                Get started
                <ArrowRight size={12} />
              </span>
            </button>
          ))}
        </div>
        <p
          data-testid="pricing-plan-availability-note"
          className="mt-4 text-center text-sm text-[#5E5C58]"
        >
          Plan availability varies by treatment.
        </p>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 md:pt-12 pb-6 md:pb-8 text-center">
        <h2 className="font-display text-3xl md:text-5xl font-semibold tracking-tight text-[#2C2B29] leading-[1.1]">
          Ready to get started?
        </h2>
        <p className="mt-3 text-[#5E5C58] text-base md:text-lg leading-relaxed max-w-2xl mx-auto">
          Choose a treatment to begin your online intake.
        </p>
        <Link
          to="/choose-your-treatment"
          data-testid="home-final-cta"
          className="mt-5 inline-flex items-center justify-center bg-[#C96A4E] text-white rounded-full px-8 py-3.5 text-base font-semibold hover:bg-[#B35A40] transition-colors shadow-sm"
        >
          Start now
          <ArrowRight size={18} className="ml-2" />
        </Link>
        <div className="mt-3">
          <a
            href={TRUSTPILOT_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="final-cta-trustpilot-link"
            className={TRUSTPILOT_LINK_CLASS}
          >
            Read our reviews on Trustpilot
          </a>
        </div>
      </section>

      <HomeFAQ />
    </div>
  );
}
